import usePermissions from '@/hooks/usePermissions';
import { OrganizationNode } from '@/types/Organizations';
import { Box, SelectInputNative } from '@palmetto/palmetto-components';
import { useEffect, useMemo } from 'react';
import { generatePath, NavLink, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  OrganizationPricingTabConfig,
  OrganizationPricingTabType,
  ProgramType,
  ProgramTypeLabels,
  programTypeOrganizationPricingTabMap,
} from 'types';

import { PricingTabOptions } from './PricingTabOptions';

const baseRoute = '/settings/organizations/:alias/pricing/:programType/:pricingType';
export const Pricing = ({ organization }: { organization?: OrganizationNode }) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { alias, pricingType, programType } = params;
  const navigate = useNavigate();

  const programTypesOptions = useMemo(() => {
    return (
      organization?.programTypes?.map((pt) => ({
        label: ProgramTypeLabels[pt],
        value: pt,
      })) || []
    );
  }, [organization]);

  const userPermissions = usePermissions();

  const programTypePricingTabs = useMemo(() => {
    const allTabs = programTypeOrganizationPricingTabMap[programType as ProgramType] || {};
    const visibleTabs: { [key in OrganizationPricingTabType]?: OrganizationPricingTabConfig } = {};
    for (const key of Object.keys(allTabs) as OrganizationPricingTabType[]) {
      const config = allTabs[key];
      const hasAccess =
        !config?.permissions?.length ||
        config.permissions.every((permission) => userPermissions.includes(permission));
      if (hasAccess) visibleTabs[key] = config;
    }
    return visibleTabs;
  }, [programType, userPermissions]);

  useEffect(() => {
    if (!organization) {
      return;
    }
    const firstTab = Object.keys(programTypePricingTabs)[0] ?? OrganizationPricingTabType.mappings;
    const validPricingType =
      params.pricingType && programTypePricingTabs[params.pricingType as OrganizationPricingTabType]
        ? params.pricingType
        : firstTab;
    const path =
      generatePath(`${baseRoute}`, {
        alias: params.alias ?? '',
        pricingType: validPricingType,
        programType: programType || '',
      }) + (searchParams?.toString() ? `?${searchParams.toString()}` : '');
    navigate(path);
  }, [organization]);

  useEffect(() => {
    if (!pricingType || !programTypePricingTabs[pricingType as OrganizationPricingTabType]) {
      const firstTab =
        (Object.keys(programTypePricingTabs)[0] as OrganizationPricingTabType) ??
        OrganizationPricingTabType.mappings;
      navigate(`${firstTab}`, { replace: true });
    }
  }, [pricingType, navigate, programTypePricingTabs]);

  const handleProgramTypeChange = (event: any) => {
    const path = generatePath(`${baseRoute}`, {
      alias: params.alias ?? '',
      pricingType: '',
      programType: event.target.value ?? '',
    });
    navigate(path);
  };

  return (
    <Box gap="md">
      {organization?.programTypes?.length && organization?.programTypes?.length > 1 && (
        <Box maxWidth="2xl">
          <SelectInputNative
            id="required"
            label="Program Type"
            value={programType || null}
            onChange={handleProgramTypeChange}
            options={programTypesOptions}
            menuPortalTarget={document.body}
            isDisabled={searchParams.size > 0}
          />
        </Box>
      )}
      <Box direction="row" childGap="3xl">
        <Box>
          {Object.keys(programTypePricingTabs).map((type) => (
            <NavLink
              to={`/settings/organizations/${alias}/pricing/${programType}/${type}`}
              style={{ textDecoration: 'none', fontWeight: '500' }}
              key={type}
              end
            >
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s', borderBottom: 0 }}
                >
                  {programTypePricingTabs[type as OrganizationPricingTabType]?.label}
                </Box>
              )}
            </NavLink>
          ))}
        </Box>
        <Box flex="auto">
          <Routes>
            <Route path="/" element={<PricingTabOptions />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};
